import { api, type CellComment, type ReaderCellComment, type SessionComment } from '@/api/client';

export const CELL_COMMENTS_PAGE_SIZE = 40;

/** Совпадает с подписью на сервере для удалённого автора комментария */
export const CELL_COMMENT_DELETED_AUTHOR_LABEL = 'Пользователь удалён';

export function cellCommentAuthorDisplayLabel(
  c:
    | Pick<CellComment, 'author_person_id' | 'author_name'>
    | Pick<ReaderCellComment, 'author_person_id' | 'author_name'>
    | Pick<SessionComment, 'author_person_id' | 'author_name'>,
  viewerPersonId: string | null
): string {
  if (viewerPersonId != null && c.author_person_id != null && c.author_person_id === viewerPersonId) {
    return 'Вы';
  }
  const name = (c.author_name ?? '').trim();
  if (name) return name;
  return c.author_person_id == null ? CELL_COMMENT_DELETED_AUTHOR_LABEL : 'Участник';
}

export type CellCommentsPage = {
  items: CellComment[];
  total: number;
  limit: number;
  offset: number;
};

export type CellCommentsPageParam = 'init' | number;

export function getCellCommentsInfiniteQueryOptions(projectId: string, columnId: string) {
  return {
    queryKey: ['cellComments', projectId, columnId] as const,
    initialPageParam: 'init' as CellCommentsPageParam,
    queryFn: async ({ pageParam }: { pageParam: CellCommentsPageParam }) => {
      if (pageParam === 'init') {
        const meta = await api.projects.listCellComments(projectId, columnId, { limit: 1, offset: 0 });
        const { total } = meta;
        if (total === 0) {
          return {
            items: [] as CellComment[],
            total: 0,
            limit: CELL_COMMENTS_PAGE_SIZE,
            offset: 0,
          };
        }
        const offset = Math.max(0, total - CELL_COMMENTS_PAGE_SIZE);
        return api.projects.listCellComments(projectId, columnId, {
          limit: CELL_COMMENTS_PAGE_SIZE,
          offset,
        });
      }

      return api.projects.listCellComments(projectId, columnId, {
        limit: CELL_COMMENTS_PAGE_SIZE,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage: CellCommentsPage) => {
      const prevOffset = lastPage.offset - CELL_COMMENTS_PAGE_SIZE;
      return prevOffset >= 0 ? prevOffset : undefined;
    },
    staleTime: 5_000,
  };
}

export type SessionCommentsPage = {
  items: SessionComment[];
  total: number;
  limit: number;
  offset: number;
};

export function getSessionCommentsInfiniteQueryOptions(sessionId: string) {
  return {
    queryKey: ['sessionComments', sessionId] as const,
    initialPageParam: 'init' as CellCommentsPageParam,
    queryFn: async ({ pageParam }: { pageParam: CellCommentsPageParam }) => {
      if (pageParam === 'init') {
        const meta = await api.sessions.listSessionComments(sessionId, { limit: 1, offset: 0 });
        const { total } = meta;
        if (total === 0) {
          return {
            items: [] as SessionComment[],
            total: 0,
            limit: CELL_COMMENTS_PAGE_SIZE,
            offset: 0,
          };
        }
        const offset = Math.max(0, total - CELL_COMMENTS_PAGE_SIZE);
        return api.sessions.listSessionComments(sessionId, {
          limit: CELL_COMMENTS_PAGE_SIZE,
          offset,
        });
      }

      return api.sessions.listSessionComments(sessionId, {
        limit: CELL_COMMENTS_PAGE_SIZE,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage: SessionCommentsPage) => {
      const prevOffset = lastPage.offset - CELL_COMMENTS_PAGE_SIZE;
      return prevOffset >= 0 ? prevOffset : undefined;
    },
    staleTime: 5_000,
  };
}

export type ReaderCellCommentsPage = {
  items: ReaderCellComment[];
  total: number;
  limit: number;
  offset: number;
};

export function getReaderCellCommentsInfiniteQueryOptions(readerPersonId: string, columnId: string) {
  return {
    queryKey: ['readerCellComments', readerPersonId, columnId] as const,
    initialPageParam: 'init' as CellCommentsPageParam,
    queryFn: async ({ pageParam }: { pageParam: CellCommentsPageParam }) => {
      if (pageParam === 'init') {
        const meta = await api.readers.listCellComments(readerPersonId, columnId, { limit: 1, offset: 0 });
        const { total } = meta;
        if (total === 0) {
          return {
            items: [] as ReaderCellComment[],
            total: 0,
            limit: CELL_COMMENTS_PAGE_SIZE,
            offset: 0,
          };
        }
        const offset = Math.max(0, total - CELL_COMMENTS_PAGE_SIZE);
        return api.readers.listCellComments(readerPersonId, columnId, {
          limit: CELL_COMMENTS_PAGE_SIZE,
          offset,
        });
      }

      return api.readers.listCellComments(readerPersonId, columnId, {
        limit: CELL_COMMENTS_PAGE_SIZE,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage: ReaderCellCommentsPage) => {
      const prevOffset = lastPage.offset - CELL_COMMENTS_PAGE_SIZE;
      return prevOffset >= 0 ? prevOffset : undefined;
    },
    staleTime: 5_000,
  };
}
